import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  DISCIPLINE_PREFERENCES,
  disciplinePreferenceLabel,
  hoursOfDay,
  useUpdateWeeklyPreferences,
  useWeeklyPreferences,
  WeeklyPreferencesSchema,
  type DisciplinePreference,
  type WeeklyPreferences,
  type WeeklyPreferencesUpdate,
} from '@/lib/profile';
import { DISCIPLINE_COLORS, type DisciplineKey } from '@/lib/disciplineColors';

/**
 * Formulario de preferencias semanales por día (M2.31.A).
 *
 * - 7 cards (Lun–Dom) con un `<select>` para elegir disciplina o "sin
 *   preferencia".
 * - Sección "Automatización" con dos toggles + selector de hora local. El
 *   toggle de nutrición queda deshabilitado si el toggle principal está OFF.
 * - Submit dispara `useUpdateWeeklyPreferences` con el patch completo (no
 *   sólo los cambios — el endpoint acepta subset pero enviar todo simplifica
 *   el flujo de "guardar" del usuario).
 *
 * Decisiones de diseño:
 * - Pre-rellena con los valores del backend cuando llegan; en loading muestra
 *   placeholder. Es deliberadamente unmounted-when-loading: evita parpadeos
 *   con valores defaults antes de la hidratación.
 * - El color del card de cada día sigue la paleta de `DISCIPLINE_COLORS`
 *   (M2.16): tinte de fondo y borde según disciplina elegida. "Descanso" y
 *   "sin preferencia" usan slate neutro.
 */
export function WeeklyPreferencesForm(): ReactElement {
  const query = useWeeklyPreferences();

  if (query.isLoading) {
    return (
      <p role="status" aria-live="polite" className="text-slate-700">
        Cargando preferencias…
      </p>
    );
  }

  if (query.isError || !query.data) {
    return (
      <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
        No se pudieron cargar las preferencias semanales.{' '}
        {query.error?.message ?? ''}
      </p>
    );
  }

  return <WeeklyPreferencesFormInner initial={query.data} />;
}

// ---------------------------------------------------------------------------
// Inner (recibe datos ya cargados → estado local sincronizado con `initial`)
// ---------------------------------------------------------------------------

interface DayDef {
  key: DayKey;
  label: string;
  field: keyof Pick<
    WeeklyPreferences,
    | 'monDiscipline'
    | 'tueDiscipline'
    | 'wedDiscipline'
    | 'thuDiscipline'
    | 'friDiscipline'
    | 'satDiscipline'
    | 'sunDiscipline'
  >;
}

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const DAYS: readonly DayDef[] = [
  { key: 'mon', label: 'Lunes',     field: 'monDiscipline' },
  { key: 'tue', label: 'Martes',    field: 'tueDiscipline' },
  { key: 'wed', label: 'Miércoles', field: 'wedDiscipline' },
  { key: 'thu', label: 'Jueves',    field: 'thuDiscipline' },
  { key: 'fri', label: 'Viernes',   field: 'friDiscipline' },
  { key: 'sat', label: 'Sábado',    field: 'satDiscipline' },
  { key: 'sun', label: 'Domingo',   field: 'sunDiscipline' },
] as const;

function WeeklyPreferencesFormInner({
  initial,
}: {
  initial: WeeklyPreferences;
}): ReactElement {
  const mutation = useUpdateWeeklyPreferences();

  const [days, setDays] = useState<Record<DayKey, DisciplinePreference | null>>(() => ({
    mon: initial.monDiscipline,
    tue: initial.tueDiscipline,
    wed: initial.wedDiscipline,
    thu: initial.thuDiscipline,
    fri: initial.friDiscipline,
    sat: initial.satDiscipline,
    sun: initial.sunDiscipline,
  }));
  const [autoWeekly, setAutoWeekly] = useState<boolean>(initial.autoGenerateWeekly);
  const [autoNutrition, setAutoNutrition] = useState<boolean>(initial.autoGenerateNutrition);
  const [hourLocal, setHourLocal] = useState<string>(
    normalizeHour(initial.autoGenerateHourLocal),
  );
  const [savedJustNow, setSavedJustNow] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Si llegan nuevos valores del server (p. ej. tras invalidación externa),
  // resincronizamos el estado local. Evitamos pisar cambios "no guardados"
  // del usuario con un flag de dirty... pero como no es crítico para esta
  // pantalla, lo dejamos simple: el server es la fuente de verdad.
  useEffect(() => {
    setDays({
      mon: initial.monDiscipline,
      tue: initial.tueDiscipline,
      wed: initial.wedDiscipline,
      thu: initial.thuDiscipline,
      fri: initial.friDiscipline,
      sat: initial.satDiscipline,
      sun: initial.sunDiscipline,
    });
    setAutoWeekly(initial.autoGenerateWeekly);
    setAutoNutrition(initial.autoGenerateNutrition);
    setHourLocal(normalizeHour(initial.autoGenerateHourLocal));
  }, [initial]);

  // Si auto-weekly se apaga, el toggle de nutrición debe verse OFF (ya no
  // aplica). Lo forzamos en el estado local para que el usuario no quede
  // confundido por un "ON" que el backend ignora.
  useEffect(() => {
    if (!autoWeekly && autoNutrition) {
      setAutoNutrition(false);
    }
  }, [autoWeekly, autoNutrition]);

  const hourOptions = useMemo(hoursOfDay, []);

  function handleDayChange(day: DayKey, raw: string) {
    const next: DisciplinePreference | null = raw === '' ? null : (raw as DisciplinePreference);
    setDays((prev) => ({ ...prev, [day]: next }));
    setSavedJustNow(false);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSavedJustNow(false);
    setValidationError(null);

    const patch: WeeklyPreferencesUpdate = {
      monDiscipline: days.mon,
      tueDiscipline: days.tue,
      wedDiscipline: days.wed,
      thuDiscipline: days.thu,
      friDiscipline: days.fri,
      satDiscipline: days.sat,
      sunDiscipline: days.sun,
      autoGenerateWeekly: autoWeekly,
      autoGenerateNutrition: autoWeekly ? autoNutrition : false,
      autoGenerateHourLocal: hourLocal,
    };

    // Validación cliente: la hora debe matchear el regex del schema. Si el
    // usuario manipula el DOM, no enviamos.
    const parsed = WeeklyPreferencesSchema.partial().safeParse(patch);
    if (!parsed.success) {
      setValidationError(
        parsed.error.issues.map((i) => i.message).join('. '),
      );
      return;
    }

    mutation.mutate(parsed.data, {
      onSuccess: () => setSavedJustNow(true),
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Configurar semana
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Define qué disciplina prefieres cada día y, opcionalmente, deja
            que el coach genere tu plan semanal automáticamente cada domingo.
          </p>
        </div>
        <Link
          to="/perfil"
          className="text-sm font-medium text-teal-700 hover:text-teal-800"
        >
          ← Volver al perfil
        </Link>
      </header>

      <section
        aria-labelledby="weekly-prefs-days-title"
        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="weekly-prefs-days-title"
          className="text-sm font-semibold uppercase tracking-wide text-slate-500"
        >
          Disciplina preferida por día
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {DAYS.map((d) => (
            <DayCard
              key={d.key}
              day={d}
              value={days[d.key]}
              onChange={(v) => handleDayChange(d.key, v)}
            />
          ))}
        </div>
      </section>

      <section
        aria-labelledby="weekly-prefs-auto-title"
        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="weekly-prefs-auto-title"
          className="text-sm font-semibold uppercase tracking-wide text-slate-500"
        >
          Automatización
        </h2>

        <div className="mt-4 space-y-4">
          <ToggleRow
            id="auto-weekly"
            label="Generar plan semanal automáticamente cada domingo"
            description="El coach IA generará el plan de la semana siguiente cada domingo a la hora indicada, usando estas preferencias y el análisis de tu semana actual."
            checked={autoWeekly}
            onChange={(v) => {
              setAutoWeekly(v);
              setSavedJustNow(false);
            }}
          />

          <ToggleRow
            id="auto-nutrition"
            label="Generar plan de nutrición tras el plan de entrenos"
            description="Cuando esté listo el plan semanal, también se generará el plan de nutrición compatible."
            checked={autoNutrition}
            disabled={!autoWeekly}
            onChange={(v) => {
              setAutoNutrition(v);
              setSavedJustNow(false);
            }}
          />

          <div>
            <label
              htmlFor="auto-hour-local"
              className="block text-sm font-medium text-slate-700"
            >
              Hora local de ejecución
            </label>
            <select
              id="auto-hour-local"
              value={hourLocal}
              onChange={(e) => {
                setHourLocal(e.target.value);
                setSavedJustNow(false);
              }}
              disabled={!autoWeekly}
              className="mt-1 block w-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {hourOptions.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              La hora se interpreta en tu zona horaria configurada en el
              perfil.
            </p>
          </div>
        </div>
      </section>

      {validationError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {validationError}
        </p>
      )}

      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudieron guardar las preferencias. {mutation.error?.message ?? ''}
        </p>
      )}

      {savedJustNow && !mutation.isError && (
        <p
          role="status"
          aria-live="polite"
          className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          Preferencias guardadas.
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar preferencias'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

function DayCard({
  day,
  value,
  onChange,
}: {
  day: DayDef;
  value: DisciplinePreference | null;
  onChange: (raw: string) => void;
}): ReactElement {
  const style = backgroundForDiscipline(value);
  const selectId = `discipline-${day.key}`;
  return (
    <div
      className="rounded-lg border p-3 shadow-sm transition-colors"
      style={style}
      data-testid={`day-card-${day.key}`}
    >
      <label
        htmlFor={selectId}
        className="block text-sm font-semibold text-slate-800"
      >
        {day.label}
      </label>
      <select
        id={selectId}
        aria-label={`Disciplina del ${day.label.toLowerCase()}`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
      >
        <option value="">— Sin preferencia —</option>
        {DISCIPLINE_PREFERENCES.map((d) => (
          <option key={d} value={d}>
            {disciplinePreferenceLabel(d)}
          </option>
        ))}
      </select>
    </div>
  );
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className={disabled ? 'opacity-60' : undefined}>
      <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-300 text-teal-600 focus:ring-teal-600 disabled:cursor-not-allowed"
        />
        <span className="flex-1">
          <span className="block text-sm font-medium text-slate-800">{label}</span>
          <span className="mt-0.5 block text-xs text-slate-600">{description}</span>
        </span>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers locales
// ---------------------------------------------------------------------------

/**
 * Devuelve el style inline para un card según la disciplina elegida.
 *
 * Usamos inline-style (en lugar de tokens Tailwind) porque las clases
 * dinámicas `bg-${tw}/15` se purgarían en build — Tailwind sólo conserva las
 * clases que ve literales en el código. Una opacidad calculada en hex evita
 * mantener un safelist.
 */
function backgroundForDiscipline(
  value: DisciplinePreference | null,
): React.CSSProperties {
  if (value === null || value === 'rest') {
    return {
      backgroundColor: '#f8fafc', // slate-50
      borderColor: '#e2e8f0',     // slate-200
    };
  }
  // `brick` y `strength` mapean 1:1 a `DisciplineKey`; resto también.
  const key = value as DisciplineKey;
  const color = DISCIPLINE_COLORS[key];
  if (!color) {
    return {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
    };
  }
  return {
    // hex + alpha 0x26 ≈ 15%.
    backgroundColor: `${color.hex}26`,
    borderColor: color.hex,
  };
}

/**
 * Normaliza horas tipo "8:00" → "08:00" para que el `<select>` encuentre la
 * opción correspondiente (sus values siempre llevan dos dígitos).
 */
function normalizeHour(h: string): string {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(h);
  if (!m) return '20:00';
  const hh = String(Number.parseInt(m[1]!, 10)).padStart(2, '0');
  return `${hh}:00`; // forzamos minutos a :00 (la UI sólo permite horas en punto)
}
